import type { RetailerProfileEntity } from '@packages/domain/retailers';
import type { TechlineAssetEntity } from '@packages/domain/techline/assets';
import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { VinDetailEntity } from '@packages/domain/vin';
import type { WorksheetFormElementEntity, WorksheetFormEntity } from '@packages/domain/worksheets/forms';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { hasSepfExtension } from '@packages/domain/techline';
import { type TechlineStatusCode } from '@packages/models/api';
import { AlertBar, TextInputHelper, Typography } from '@packages/ui/shared';
import { WorksheetUploadWarningModal } from '@packages/ui/worksheet/components/worksheet-upload-warning-modal';
import { useWorksheetNavigationGuard } from '@packages/ui/worksheet/hooks/use-worksheet-navigation-guard';
import Loader from '@web/components/loader';
import { analyticsService } from '@web/services/singletons';
import { downloadFile } from '@web/utils';
import { getLatestWorksheetSubmissionIds } from '@web/utils/case-utils';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import { AttachmentList } from '../../attachment-list/attachment-list';
import { TechlineHeader } from '../../techline-header';
import SubaruEcmImage from './assets/subaru-ecm.png';
import { BackModal } from './back-modal';
import { DeleteCaseModal } from './delete-case-modal';
import { type CidCvnCaseFormData, validateAllFields, validateField } from './helpers';
import { SubmitConfirmationModal } from './submit-confirmation-modal';

const logger = getLogger('techline');

const WORKSHEET_CASE_TYPE_CID_CVN = 'CID/CVN';
const INPUT_TEXT_TYPE = 'INPUT_TEXT' as const;

const dataValidation = {
    maxMileage: 100000000000,
};

const DROP_ZONE_LABEL = 'Click or drag to upload your .SEPF file';
const DROP_ZONE_HINT = '.SEPF file only';

/**
 * Props for the CidCvnWorksheetSubview component.
 *
 * All data flows down as props and all user actions bubble up as `on*` callbacks.
 * The component never calls services or APIs directly.
 */
export type CidCvnWorksheetSubviewProps = {
    /** Case-level file attachments (complete or still uploading). */
    assetData: TechlineAssetEntity[];

    /** Error message from a failed asset upload or processing. Empty when no error. */
    assetErrorMessage: string;

    /** The full case details object from NorthStar. */
    caseDetails: TechlineCaseEntity | undefined;

    /** The case number identifying this techline case. */
    caseNumber: string;

    /** Dismiss the asset error alert bar. */
    onAssetErrorDismiss: () => void;

    /** Delete a case-level asset by ID. */
    onDeleteAsset: (assetId: string) => Promise<void>;

    /** Delete the current CID-CVN draft case. Returns true on success. */
    onDeleteCase: () => Promise<boolean>;

    /** Fetch an existing worksheet submission by case number and submission ID. */
    onFetchWorksheetSubmission: (caseNumber: string, submissionId: string) => Promise<WorksheetFormEntity | undefined>;

    /** Re-fetch case-level assets from the API. */
    onRefreshAssets: () => Promise<void>;

    /** Save CID-CVN case details (repair order, repair order date, mileage). */
    onSaveCase: (formData: CidCvnCaseFormData) => Promise<void>;

    /** Submit the CID-CVN case for review (status -> IN_REVIEW). */
    onSubmitCase: (submissionId: string, worksheetId: string) => Promise<void>;

    /** Upload a file as a case-level asset. */
    onUploadAsset: (file: File) => Promise<void>;

    /** The primary retailer profile for this case. */
    primaryRetailerData: RetailerProfileEntity | undefined;

    /** VIN details for the vehicle associated with the case. */
    vehicleVinData: VinDetailEntity | undefined;

    /** The worksheet submission data for the CID-CVN worksheet, managed by the parent hook. */
    worksheetSubmissionData: WorksheetFormEntity | undefined;

    /** Whether the worksheet submission data is currently being fetched. */
    worksheetSubmissionFetching: boolean;
};

/** Alert bar message state shape. */
type AlertBarMessage = {
    /** The message text to display in the alert bar. */
    message: string;
};

/** Shared label style for read-only worksheet element fields. */
const readOnlyLabelStyle = { fontWeight: 400 as const };

/** Shared text input style for read-only worksheet element fields. */
const readOnlyTextInputStyle = { fontSize: 15, fontWeight: 400 as const };

/** Shared input container style for disabled/read-only fields. */
const readOnlyInputContainerStyle = {
    backgroundColor: colors.grayThree,
    borderColor: colors.grayFive,
    borderStyle: 'solid' as const,
    borderWidth: 1,
};

/** Shared label style for editable form fields. */
const editableLabelStyle = { fontWeight: 400 as const };

/** Shared text input style for editable form fields. */
const editableTextInputStyle = { fontSize: 15, fontWeight: 400 as const };

/**
 * Presentational subview for CID-CVN worksheet cases. Renders the case form
 * (repair order details, worksheet elements, file attachments) and modals
 * for save/delete/submit confirmation.
 *
 * All API interactions are delegated to parent callbacks; this component owns
 * only local form state, validation, modal state, and UI feedback.
 *
 * @param props - component props
 * @returns the CID-CVN worksheet subview
 */
export function CidCvnWorksheetSubview({
    assetData,
    assetErrorMessage,
    caseDetails,
    caseNumber,
    onAssetErrorDismiss,
    onDeleteAsset,
    onDeleteCase,
    onFetchWorksheetSubmission,
    onRefreshAssets,
    onSaveCase,
    onSubmitCase,
    onUploadAsset,
    primaryRetailerData,
    vehicleVinData: _vehicleVinData,
    worksheetSubmissionData,
    worksheetSubmissionFetching,
}: CidCvnWorksheetSubviewProps) {
    // State
    const [activeModalType, setActiveModalType] = useState<'back' | 'deleteCase' | 'submitCase' | null>(null);
    const [alertBarMessage, setAlertBarMessage] = useState<AlertBarMessage>({ message: '' });
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [errors, setErrors] = useState<Partial<Record<keyof CidCvnCaseFormData, string>>>({});
    const [formData, setFormData] = useState<CidCvnCaseFormData>({
        mileage: caseDetails?.mileage ? String(caseDetails.mileage) : '',
        repairOrderNumber: caseDetails?.repairOrderNumber || '',
        repairOrderOpenDate: caseDetails?.repairOrderOpenDate ? caseDetails.repairOrderOpenDate.split('T')[0] : '',
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDraftSaving, setIsDraftSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidationAlertBar, setShowValidationAlertBar] = useState<boolean>(false);
    const [touched, setTouched] = useState<Partial<Record<keyof CidCvnCaseFormData, boolean>>>({});

    // Hooks
    const classes = useStyles();
    const navigate = useNavigate();

    // Navigation guard to warn about uploads in progress
    // Note: This checks worksheet submission elements which may contain file uploads
    const {
        hasUploadsInProgress: _hasUploadsInProgress,
        showNavigationModal,
        warningMessage,
        handleConfirmNavigation,
        handleCancelNavigation,
    } = useWorksheetNavigationGuard(worksheetSubmissionData?.elements);
    // Forwarded callback aliases
    const handleAssetErrorDismiss = onAssetErrorDismiss;

    const { worksheetId, submissionId } = useMemo(() => {
        if (!caseDetails?.worksheetSubmissions?.length) {
            return { worksheetId: '', submissionId: '' };
        }
        return getLatestWorksheetSubmissionIds(caseDetails, WORKSHEET_CASE_TYPE_CID_CVN);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseDetails?.worksheetSubmissions]);

    /**
     * Navigate back to the previous page, or to the dashboard if there is no history.
     */
    const navigateBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(`/techline/dashboard?retailerNumber=${primaryRetailerData?.retailerNumber || ''}`);
        }
    }, [navigate, primaryRetailerData]);

    // Derived state
    const isDraftCase = caseDetails?.techlineStatusCode === ('DRAFT' as TechlineStatusCode);
    const areButtonsDisabled = isDraftSaving || isSubmitting || isDeleting;
    const isFieldEditable = !areButtonsDisabled && isDraftCase;
    const isSubmitButtonDisabled =
        !formData.mileage || !formData.repairOrderNumber || !formData.repairOrderOpenDate || areButtonsDisabled;

    const missingIds = !worksheetId || !submissionId;

    /** Filter assetData to only include .sepf files. */
    const sepfAssetData = useMemo(() => assetData.filter((asset) => hasSepfExtension(asset.name)), [assetData]);

    const showLoader = !caseDetails || missingIds || worksheetSubmissionFetching || !worksheetSubmissionData;

    /**
     * Extract and sort INPUT_TEXT worksheet elements for display
     * as read-only fields showing the initial CID/CVN check values.
     */
    const worksheetElements = useMemo(() => {
        if (!worksheetSubmissionData?.elements || !Array.isArray(worksheetSubmissionData.elements)) {
            return [];
        }
        return (worksheetSubmissionData.elements as WorksheetFormElementEntity[])
            .filter((element) => element?.worksheetElementTypeId === INPUT_TEXT_TYPE)
            .sort((a, b) => (a?.worksheetElementOrder ?? 0) - (b?.worksheetElementOrder ?? 0))
            .map((element) => {
                const id = element?.worksheetElementId || 'N/A';
                const label = element?.worksheetElementLabel || 'N/A';
                const order = element?.worksheetElementOrder ?? 0;
                const selectedOptions = element?.selectedOptions || [];
                const answer = selectedOptions[0]?.answerText || '';
                const answerId = selectedOptions[0]?.optionId || '';
                return {
                    id,
                    label,
                    answer,
                    answerId,
                    order,
                };
            });
    }, [worksheetSubmissionData]);

    // Handlers
    /**
     * Update a single form field and validate it. If the field has been touched (blurred),
     * the error message will be updated live as the user types.
     * Untouched fields will not show errors until first blur.
     *
     * @param field - the form field key to update
     * @param value - the new value
     */
    const handleFieldChange = useCallback(
        (field: keyof CidCvnCaseFormData, value: string) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            const error = validateField(field, value);

            setErrors((prev) => {
                if (touched[field]) {
                    return { ...prev, [field]: error };
                }
                return !error && prev[field] ? { ...prev, [field]: undefined } : prev;
            });
        },
        [touched]
    );

    /**
     * Blur pipeline for form fields: trims the value, commits the trimmed
     * value, marks the field as touched, and validates the trimmed value.
     *
     * @param field - the form field key that lost focus
     * @param value - the field value to validate
     */
    const handleFieldBlur = useCallback((field: keyof CidCvnCaseFormData, value: string) => {
        const trimmedValue = value.trim();

        setFormData((prev) => ({ ...prev, [field]: trimmedValue }));
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors((prev) => ({ ...prev, [field]: validateField(field, trimmedValue) }));
    }, []);

    /**
     * Handle mileage input changes by stripping non-numeric characters
     * and enforcing the maximum mileage limit.
     *
     * @param text - the raw input text
     */
    const handleMileageFieldTextChange = useCallback(
        (text: string) => {
            const numeric = text.replace(/[^0-9]/g, '');
            if (numeric === '' || Number.parseInt(numeric, 10) <= dataValidation.maxMileage) {
                handleFieldChange('mileage', numeric);
            }
        },
        [handleFieldChange]
    );

    // -- Event handlers --

    /** Open the back-navigation confirmation modal. */
    const handleBackButtonClick = useCallback(() => {
        setActiveModalType('back');
    }, []);

    /** Dismiss the currently open modal and clear its alert message. */
    const handleCancelModalButtonClick = useCallback(() => {
        setActiveModalType(null);
        setAlertBarMessage({ message: '' });
    }, []);

    /**
     * Handle the delete button click in the delete confirmation modal.
     * Delegates to the onDeleteCase callback, logs the event, clears
     * the draft cache, and navigates back on success.
     */
    const handleDeleteButtonClick = useCallback(async () => {
        setIsDeleting(true);
        try {
            const success = await onDeleteCase();
            if (!success) {
                setAlertBarMessage({
                    message: 'We encountered an error while deleting your case. Please try again.',
                });
                setIsDeleting(false);
                return;
            }
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_DELETES_TECHLINE_DRAFT);
            navigateBack();
            setActiveModalType(null);
        } catch {
            setAlertBarMessage({
                message: 'We encountered an error while deleting your case. Please try again.',
            });
        }
        setIsDeleting(false);
    }, [navigateBack, onDeleteCase]);

    /** Open the delete case confirmation modal. */
    const handleDeleteCaseButtonClick = useCallback(() => {
        setActiveModalType('deleteCase');
    }, []);

    /**
     * Handle deleting a case-level asset. Delegates to the onDeleteAsset
     * callback and refreshes the asset list afterward.
     *
     * @param assetId - the ID of the asset to delete
     */
    const handleAttachmentListDelete = useCallback(
        async (assetId: string) => {
            try {
                await onDeleteAsset(assetId);
                await onRefreshAssets();
            } catch (error) {
                logger.error(`Failed to delete asset [${assetId}]`, { assetId, error });
                setErrorMessage('Failed to delete file. Please try again.');
                setTimeout(() => setErrorMessage(''), 3000);
                onRefreshAssets();
            }
        },
        [onDeleteAsset, onRefreshAssets]
    );

    /**
     * Handle downloading a case-level asset by ID. Locates the asset's
     * download URL from the asset data and triggers a browser download.
     *
     * @param assetId - the ID of the asset to download
     */
    const handleAttachmentListDownload = useCallback(
        async (assetId: string) => {
            const asset = assetData.find((a) => a.id === assetId);
            if (!asset) {
                return;
            }

            if (!asset.downloadUrl) {
                setErrorMessage('Download URL not available. Please try again later.');
                setTimeout(() => setErrorMessage(''), 3000);
                return;
            }

            try {
                await downloadFile(asset.downloadUrl, asset.name || 'attachment');
            } catch {
                setErrorMessage('Failed to download file. Please try again later.');
                setTimeout(() => setErrorMessage(''), 3000);
            }
        },
        [assetData]
    );

    /** Clear the error alert bar message. */
    const handleErrorAlertBarClose = useCallback(() => {
        setErrorMessage('');
    }, []);

    /** Navigate back without saving from the back modal. */
    const handleExitWithoutSaving = useCallback(() => {
        navigateBack();
    }, [navigateBack]);

    /** Clear the modal alert bar message. */
    const handleModalAlertBarClose = useCallback(() => {
        setAlertBarMessage({ message: '' });
    }, []);

    /**
     * Handle the mileage field blur by stripping leading zeros,
     * updating the field value, and running validation.
     */
    const handleMileageFieldBlur = useCallback(() => {
        const cleaned = formData.mileage.replace(/^0+/, '');
        handleFieldBlur('mileage', cleaned);
    }, [formData.mileage, handleFieldBlur]);

    /**
     * Handle the repair order date field text change.
     *
     * @param text - the new date field value
     */
    const handleRepairOrderDateFieldTextChange = useCallback(
        (text: string) => {
            handleFieldChange('repairOrderOpenDate', text);
        },
        [handleFieldChange]
    );

    /** Handle the repair order date field blur. */
    const handleRepairOrderDateFieldBlur = useCallback(() => {
        handleFieldBlur('repairOrderOpenDate', formData.repairOrderOpenDate);
    }, [formData.repairOrderOpenDate, handleFieldBlur]);

    /**
     * Handle the repair order number field text change.
     *
     * @param text - the new repair order number value
     */
    const handleRepairOrderNumberFieldTextChange = useCallback(
        (text: string) => {
            handleFieldChange('repairOrderNumber', text);
        },
        [handleFieldChange]
    );

    /** Handle the repair order number field blur. */
    const handleRepairOrderNumberFieldBlur = useCallback(() => {
        handleFieldBlur('repairOrderNumber', formData.repairOrderNumber);
    }, [formData.repairOrderNumber, handleFieldBlur]);

    /**
     * Handle the "Save and Exit" button click within the back-navigation modal.
     * Saves the case, clears the draft cache, and navigates back.
     */
    const handleSaveAndExitModalButtonClick = useCallback(async () => {
        setIsDraftSaving(true);
        try {
            await onSaveCase(formData);
            navigateBack();
        } catch (error) {
            logger.error('Failed to save draft case', { error });
            setAlertBarMessage({
                message: (error as Error).message,
            });
        }
        setIsDraftSaving(false);
    }, [formData, navigateBack, onSaveCase]);

    /**
     * Handle the "Save and Exit" button click on the header toolbar.
     * Saves the case, clears the draft cache, and navigates back.
     */
    const handleSaveAndExitHeaderButtonClick = useCallback(async () => {
        setIsDraftSaving(true);
        try {
            await onSaveCase(formData);
            navigateBack();
        } catch (error) {
            logger.error('Failed to save draft case', { error });
            setErrorMessage((error as Error).message);
            setTimeout(() => setErrorMessage(''), 3000);
        }
        setIsDraftSaving(false);
    }, [formData, navigateBack, onSaveCase]);

    /**
     * Handle the Submit button click. Validates all fields and opens
     * the submit confirmation modal if valid. Otherwise, shows the validation
     * alert bar and marks all fields as touched.
     */
    const handleSubmitButtonClick = useCallback(() => {
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_CLICKS_SUBMIT_TECHLINE_CASE);

        const newErrors = validateAllFields(formData);
        const isFormValid = !Object.values(newErrors).some(Boolean);

        if (!isFormValid) {
            setErrors(newErrors);
            setTouched({ mileage: true, repairOrderNumber: true, repairOrderOpenDate: true });
            setShowValidationAlertBar(true);
            setTimeout(() => {
                setShowValidationAlertBar(false);
            }, 5000);
            return;
        }
        setActiveModalType('submitCase');
    }, [formData]);

    /**
     * Handle the confirm button click in the submit confirmation modal.
     * Saves the case, then submits it for review.
     */
    const handleSubmitConfirmationButtonClick = useCallback(async () => {
        setIsSubmitting(true);
        try {
            await onSaveCase(formData);
            await onSubmitCase(submissionId, worksheetId);
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_SUBMITS_TECHLINE_CASE);
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_CASE_CREATED, {
                device_type: 'Web',
                case_number: caseNumber,
            });
            navigate(`/techline/dashboard?showSubmitCaseSuccessFor=${caseNumber}`);
        } catch (error) {
            logger.error(`Failed to submit case [${caseNumber}]`, { caseNumber, error });
            setAlertBarMessage({
                message: (error as Error).message,
            });
        }
        setIsSubmitting(false);
    }, [caseNumber, formData, navigate, onSaveCase, onSubmitCase, submissionId, worksheetId]);

    /**
     * Handle uploading files as case-level assets. Validates that each file
     * has a .sepf extension, then delegates valid files to the onUploadAsset callback.
     *
     * @param files - the files to upload
     */
    const handleAttachmentListUpload = useCallback(
        (files: File[]) => {
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_STARTS_ATTACHMENT_FLOW);

            files.forEach(async (file) => {
                // Validate SEPF extension
                if (!hasSepfExtension(file.name)) {
                    setErrorMessage('Invalid file type. Please upload a .SEPF file.');
                    setTimeout(() => setErrorMessage(''), 3000);
                    return;
                }

                try {
                    await onUploadAsset(file);
                    analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_UPLOADS_ATTACHMENT);
                } catch {
                    setErrorMessage('File uploading failed. Please try again!');
                    setTimeout(() => setErrorMessage(''), 5000);
                }
            });
        },
        [onUploadAsset]
    );

    /**
     * Validate that a file has a .sepf extension before accepting it
     * in the attachment list drop zone.
     *
     * @param file - the file to validate
     * @returns true if the file has a .sepf extension
     */
    const handleAttachmentListValidateFile = useCallback((file: File) => {
        return hasSepfExtension(file.name);
    }, []);

    /** Hide the validation alert bar. */
    const handleValidationAlertBarClose = useCallback(() => {
        setShowValidationAlertBar(false);
    }, []);

    // Effects

    /** Fetch worksheet submission data when IDs become available. */
    useEffect(() => {
        if (worksheetId && submissionId) {
            onFetchWorksheetSubmission(caseNumber, submissionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [worksheetId, submissionId, caseNumber]);

    /** Sync form data when case details change (e.g. after refetch). */
    useEffect(() => {
        setFormData({
            mileage: caseDetails?.mileage ? String(caseDetails.mileage) : '',
            repairOrderNumber: caseDetails?.repairOrderNumber || '',
            repairOrderOpenDate: caseDetails?.repairOrderOpenDate ? caseDetails.repairOrderOpenDate.split('T')[0] : '',
        });
    }, [caseDetails]);

    return showLoader ? (
        <Loader />
    ) : (
        <div className={classes.container}>
            <div className={classes.stickyTop}>
                <TechlineHeader
                    caseDetails={caseDetails}
                    caseNumber={caseNumber}
                    disabledDelete={areButtonsDisabled}
                    disabledSave={areButtonsDisabled}
                    disabledSubmit={isSubmitButtonDisabled}
                    disablePrimaryBtn={true}
                    isSaveLoading={isDraftSaving && activeModalType === null}
                    onBack={handleBackButtonClick}
                    onDelete={handleDeleteCaseButtonClick}
                    onSaveAndExit={handleSaveAndExitHeaderButtonClick}
                    onSubmit={handleSubmitButtonClick}
                />
                {showValidationAlertBar && (
                    <AlertBar error onClose={handleValidationAlertBarClose} show>
                        <Typography>Please correct the highlighted errors before submitting the case.</Typography>
                    </AlertBar>
                )}

                {errorMessage && (
                    <div>
                        <AlertBar error onClose={handleErrorAlertBarClose} show>
                            <Typography>{errorMessage}</Typography>
                        </AlertBar>
                    </div>
                )}

                {assetErrorMessage && (
                    <div>
                        <AlertBar error onClose={handleAssetErrorDismiss} show>
                            <Typography>{assetErrorMessage}</Typography>
                        </AlertBar>
                    </div>
                )}
            </div>

            <div className={classes.content}>
                <div className="mb-6">
                    <Typography variant="h3">CID/CVN Confirmation</Typography>
                </div>
                <div className="">
                    <Typography variant="label">CID/CVN Investigation</Typography>
                </div>
                <div className="mb-4">
                    <Typography>
                        We could not find the CID you provided in our database. Please provide additional information so
                        Techline can investigate.
                    </Typography>
                </div>
                <img alt="Subaru ECM" className={classes.image} src={SubaruEcmImage} />
                <div className="mb-6 mt-8">
                    <Typography variant="label">Numbers from initial CID/CVN check</Typography>
                </div>

                <div className="d-flex flex-column flex-md-row mb-6 gap-5">
                    <div className={classes.flexOne}>
                        <TextInputHelper
                            editable={false}
                            inputContainerStyle={readOnlyInputContainerStyle}
                            label={worksheetElements[0]?.label}
                            labelStyle={readOnlyLabelStyle}
                            textInputStyle={readOnlyTextInputStyle}
                            value={worksheetElements[0]?.answer}
                        />
                    </div>
                    <div className={classes.flexOne}>
                        <TextInputHelper
                            editable={false}
                            inputContainerStyle={readOnlyInputContainerStyle}
                            label={worksheetElements[1]?.label}
                            labelStyle={readOnlyLabelStyle}
                            textInputStyle={readOnlyTextInputStyle}
                            value={worksheetElements[1]?.answer}
                        />
                    </div>
                </div>

                <div className={classes.divider} />

                <div>
                    <div className="mb-4">
                        <Typography variant="label">
                            Provide the following information to submit your case:
                            <Typography color="redOne"> *</Typography>
                        </Typography>
                    </div>
                    <div className="d-flex flex-column flex-md-row mb-6 gap-5">
                        <div className={`${classes.flexOne}`}>
                            <TextInputHelper
                                editable={isFieldEditable}
                                errorMessage={touched.repairOrderNumber ? errors.repairOrderNumber : undefined}
                                label="Repair Order Number:"
                                labelStyle={editableLabelStyle}
                                onBlur={handleRepairOrderNumberFieldBlur}
                                onChangeText={handleRepairOrderNumberFieldTextChange}
                                placeholder="Enter RO number"
                                textInputStyle={editableTextInputStyle}
                                value={formData.repairOrderNumber}
                            />
                        </div>
                        <div className={`${classes.flexOne}`}>
                            <TextInputHelper
                                editable={isFieldEditable}
                                errorMessage={touched.repairOrderOpenDate ? errors.repairOrderOpenDate : undefined}
                                label="Repair Order Date:"
                                labelStyle={editableLabelStyle}
                                maxDate={format(new Date(), 'yyyy-MM-dd')}
                                minDate="1753-01-01"
                                onBlur={handleRepairOrderDateFieldBlur}
                                onChangeText={handleRepairOrderDateFieldTextChange}
                                placeholder="mm/dd/yyyy"
                                textInputStyle={editableTextInputStyle}
                                type="date"
                                value={formData.repairOrderOpenDate}
                            />
                        </div>
                        <div className={classes.flexOne}>
                            <TextInputHelper
                                editable={isFieldEditable}
                                errorMessage={touched.mileage ? errors.mileage : undefined}
                                label="Mileage:"
                                labelStyle={editableLabelStyle}
                                onBlur={handleMileageFieldBlur}
                                onChangeText={handleMileageFieldTextChange}
                                placeholder="Enter mileage"
                                textInputStyle={editableTextInputStyle}
                                value={formData.mileage}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="mb-4">
                            <Typography>
                                Upload the required .SEPF file generated by your Subaru Select Monitor Application.
                            </Typography>
                        </div>
                        <AttachmentList
                            assetData={sepfAssetData}
                            dropzoneHint={DROP_ZONE_HINT}
                            dropzoneLabel={DROP_ZONE_LABEL}
                            onDelete={handleAttachmentListDelete}
                            onDownload={handleAttachmentListDownload}
                            onUpload={handleAttachmentListUpload}
                            validateFile={handleAttachmentListValidateFile}
                        />
                    </div>
                </div>
            </div>

            <BackModal
                alertBarMessage={alertBarMessage.message}
                isDraftSaving={isDraftSaving}
                isVisible={activeModalType === 'back'}
                onAlertBarClose={handleModalAlertBarClose}
                onCancel={handleCancelModalButtonClick}
                onExitWithoutSaving={handleExitWithoutSaving}
                onSaveAndExit={handleSaveAndExitModalButtonClick}
            />
            <DeleteCaseModal
                alertBarMessage={alertBarMessage.message}
                isDeleting={isDeleting}
                isVisible={activeModalType === 'deleteCase'}
                onAlertBarClose={handleModalAlertBarClose}
                onCancel={handleCancelModalButtonClick}
                onDelete={handleDeleteButtonClick}
            />
            <SubmitConfirmationModal
                alertBarMessage={alertBarMessage.message}
                isSubmitting={isSubmitting}
                isVisible={activeModalType === 'submitCase'}
                onAlertBarClose={handleModalAlertBarClose}
                onCancel={handleCancelModalButtonClick}
                onSubmit={handleSubmitConfirmationButtonClick}
            />

            {/* Navigation warning modal for uploads in progress */}
            <WorksheetUploadWarningModal
                message={warningMessage}
                onCancel={handleCancelNavigation}
                onConfirm={handleConfirmNavigation}
                visible={showNavigationModal}
            />
        </div>
    );
}

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.grayTwo,
        minHeight: '100vh',
        paddingBottom: 64,
    },
    content: {
        backgroundColor: colors.white,
        maxWidth: '800px',
        padding: '20px',
        margin: '32px auto 0',
        borderRadius: '8px',
    },
    divider: {
        height: '1px',
        margin: '40px 0',
        backgroundColor: colors.grayThree,
    },
    flexOne: {
        flex: 1,
    },
    image: {
        height: '144px',
        width: '100%',
        objectFit: 'cover',
    },
    inner: {
        alignContent: 'center',
        display: 'flex',
        flexDirection: 'column',
    },
    stickyTop: {
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        background: colors.grayTwo,
    },
});
